import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import AccordionDetails from "@mui/material/AccordionDetails"
import AccordionSummary from "@mui/material/AccordionSummary"
import Button from "@mui/material/Button"
import Link from "@mui/material/Link"
import Skeleton from "@mui/material/Skeleton"
import Switch from "@mui/material/Switch"
import Typography from "@mui/material/Typography"
import Grid from "@mui/material/Unstable_Grid2"
import { useDebounceCallback } from "usehooks-ts"

import { useAuthorizedAxiosClient } from "../../../../../common/axios"
import Column, {
  CenterEverythingColumn,
} from "../../../../../common/components/Column"
import Row from "../../../../../common/components/Row"
import { formatDateTime } from "../../../../../common/utils"
import { ColorCodedPriority } from "../../../components/TaskDialog"
import { TASK_TYPES } from "../../../tasks/utils"
import { isTaskOverdue } from "../../../utils"
import {
  AccordionDetailsContent,
  AccordionSummaryContent,
  StyledAccordion,
} from "./Accordions"
import { HasNewChip } from "./EmailsTab"
import {
  AccordionDetailsTitleValue,
  AccordionDetailsValueTypography,
} from "./MeetingScheduleTab"
import { performedBySubtitle } from "./PerformedByAttribution"
import TabColumn from "./TabColumn"
import TabContentColumn from "./TabContentColumn"
import TabHeaderRow from "./TabHeaderRow"

export default function TasksTab({
  tasks,
  setTasks,
  onTaskClicked,
  onAddTaskButtonClicked,
  onCRMEventsChanged,
  isLoading = false,
  readOnly = false,
}) {
  const axios = useAuthorizedAxiosClient()

  function handleTaskLinkClicked(e, task) {
    e.preventDefault()
    e.stopPropagation()
    onTaskClicked(task)
  }

  const debouncedUpdateTaskCompleted = useDebounceCallback(
    (task, checked) =>
      axios
        .put(`/crm/api/tasks/${task.id}/`, { completed: checked })
        .then(() => onCRMEventsChanged?.()),
    3000
  )

  function handleCompletedSwitchChanged(checked, task) {
    debouncedUpdateTaskCompleted(task, checked)
    setTasks(
      tasks.map((task_) =>
        task_.id === task.id ? { ...task, completed: checked } : task_
      )
    )
  }

  return (
    <TabColumn>
      <TabHeaderRow title="Tasks">
        {readOnly ? null : (
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<AssignmentOutlinedIcon />}
            onClick={onAddTaskButtonClicked}
          >
            Add task
          </Button>
        )}
      </TabHeaderRow>
      <TabContentColumn>
        {isLoading ? (
          <TaskSkeleton items={5} />
        ) : tasks.length === 0 ? (
          <CenterEverythingColumn>
            <Typography variant="body1" color="text.secondary">
              No tasks have been added yet.
            </Typography>
          </CenterEverythingColumn>
        ) : (
          tasks.map((task) => (
            <StyledAccordion defaultExpanded key={task.id}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  "& .MuiAccordionSummary-content": {
                    justifyContent: "space-between",
                  },
                }}
              >
                <AccordionSummaryContent
                  Icon={<AssignmentOutlinedIcon />}
                  accordionTitle={
                    <Row sx={{ alignItems: "center", gap: 1, height: "20px" }}>
                      <Link
                        href="#"
                        color="inherit"
                        onClick={(e) => handleTaskLinkClicked(e, task)}
                      >
                        {task.title}
                      </Link>
                      {isTaskOverdue(task) && (
                        <HasNewChip color="error" label="overdue" />
                      )}
                    </Row>
                  }
                  dateStr={task.created}
                  subtitle={performedBySubtitle(task)}
                />
              </AccordionSummary>
              <AccordionDetails>
                <AccordionDetailsContent sx={{ flexDirection: "column" }}>
                  <Grid
                    container
                    rowSpacing={2}
                    columnSpacing={2}
                    sx={{ width: "100%" }}
                  >
                    <Grid xs={5}>
                      <AccordionDetailsTitleValue title="Due date">
                        <AccordionDetailsValueTypography>
                          {formatDateTime(task.due_datetime)}
                        </AccordionDetailsValueTypography>
                      </AccordionDetailsTitleValue>
                    </Grid>
                    <Grid xs={5}>
                      <AccordionDetailsTitleValue title="Reminder date">
                        {task.reminder_datetime ? (
                          <AccordionDetailsValueTypography>
                            {formatDateTime(task.reminder_datetime)}
                          </AccordionDetailsValueTypography>
                        ) : (
                          "-"
                        )}
                      </AccordionDetailsTitleValue>
                    </Grid>
                    <Grid xs={2}>
                      <AccordionDetailsTitleValue title="Completed">
                        <Switch
                          checked={task.completed}
                          disabled={readOnly}
                          onChange={(e, checked) =>
                            handleCompletedSwitchChanged(checked, task)
                          }
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            "& .MuiSwitch-track": {
                              backgroundColor: (theme) =>
                                theme.palette.colors.gray300,
                            },
                          }}
                        />
                      </AccordionDetailsTitleValue>
                    </Grid>
                    <Grid xs={5}>
                      <AccordionDetailsTitleValue title="Type">
                        <AccordionDetailsValueTypography>
                          {TASK_TYPES[task.type]}
                        </AccordionDetailsValueTypography>
                      </AccordionDetailsTitleValue>
                    </Grid>
                    <Grid xs={5}>
                      <AccordionDetailsTitleValue title="Priority">
                        <ColorCodedPriority priority={task.priority} />
                      </AccordionDetailsTitleValue>
                    </Grid>
                    <Grid xs={12}>
                      <AccordionDetailsTitleValue title="Notes">
                        {task.notes || "-"}
                      </AccordionDetailsTitleValue>
                    </Grid>
                  </Grid>
                </AccordionDetailsContent>
              </AccordionDetails>
            </StyledAccordion>
          ))
        )}
      </TabContentColumn>
    </TabColumn>
  )
}

function TaskSkeleton({ items = 5 }) {
  return (
    <>
      {Array.from({ length: items }).map((_, i) => (
        <StyledAccordion key={i} disabled defaultExpanded>
          <AccordionSummary
            expandIcon={<Skeleton variant="circular" width={24} height={24} />}
            sx={{
              "& .MuiAccordionSummary-content": {
                justifyContent: "space-between",
              },
            }}
          >
            <Row sx={{ gap: 2, alignItems: "center", width: "100%" }}>
              <Skeleton variant="circular" width={32} height={32} />
              <Column sx={{ gap: 0.5, flex: 1 }}>
                <Row sx={{ gap: 1, alignItems: "center" }}>
                  <Skeleton variant="text" width={180} height={24} />
                  {i % 3 === 0 && (
                    <Skeleton
                      variant="rectangular"
                      width={52}
                      height={18}
                      sx={{ borderRadius: "9px" }}
                    />
                  )}
                </Row>
                <Skeleton variant="text" width={140} height={14} />
              </Column>
            </Row>
          </AccordionSummary>
          <AccordionDetails>
            <AccordionDetailsContent sx={{ flexDirection: "column" }}>
              <Grid
                container
                rowSpacing={2}
                columnSpacing={2}
                sx={{ width: "100%" }}
              >
                <Grid xs={5}>
                  <Column gap={1}>
                    <Skeleton variant="text" width={80} height={20} />
                    <Skeleton variant="text" width={140} height={24} />
                  </Column>
                </Grid>
                <Grid xs={5}>
                  <Column gap={1}>
                    <Skeleton variant="text" width={100} height={20} />
                    <Skeleton variant="text" width={140} height={24} />
                  </Column>
                </Grid>
                <Grid xs={2}>
                  <Column gap={1}>
                    <Skeleton variant="text" width={70} height={20} />
                    <Skeleton
                      variant="rectangular"
                      width={44}
                      height={24}
                      sx={{ borderRadius: "12px" }}
                    />
                  </Column>
                </Grid>
                <Grid xs={5}>
                  <Column gap={1}>
                    <Skeleton variant="text" width={40} height={20} />
                    <Skeleton variant="text" width={120} height={24} />
                  </Column>
                </Grid>
                <Grid xs={5}>
                  <Column gap={1}>
                    <Skeleton variant="text" width={55} height={20} />
                    <Skeleton
                      variant="rectangular"
                      width={60}
                      height={24}
                      sx={{ borderRadius: "4px" }}
                    />
                  </Column>
                </Grid>
                <Grid xs={12}>
                  <Column gap={1}>
                    <Skeleton variant="text" width={50} height={20} />
                    <Skeleton variant="text" width="100%" height={20} />
                    <Skeleton variant="text" width="80%" height={20} />
                  </Column>
                </Grid>
              </Grid>
            </AccordionDetailsContent>
          </AccordionDetails>
        </StyledAccordion>
      ))}
    </>
  )
}
